/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { AlertColor } from '@mui/material';
import { LinearProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import {
  AlertBanner,
  useConfirmationDialog,
  usePreventBrowserClose,
  usePrevious,
  dateUtils,
} from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { MaritimeWarningsForm } from './MaritimeWarningsForm';
import type {
  MaritimeWarningItemAttributes,
  MaritimeWarningsDomain,
} from './types';
import {
  reducer,
  useMaritimeWarningsFormState,
} from './useMaritimeWarningsFormState';
import {
  getType,
  mapItemsToMaritimeWarnings,
  mapMaritimeWarningsToItems,
} from './utils';
import {
  useMutateMaritimeWarningsEditor,
  useMaritimeWarningsConfig,
  usePostMaritimeWarnings,
  usePublicWarnings,
  BE_EDITOR_WARNING,
} from '../../api/warning-api/hooks';
import type { MaritimeWarning } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';

export interface MaritimeWarningsFormConnectProps {
  domain: MaritimeWarningsDomain;
  onPreventCloseView?: (on: boolean) => void;
  onModifyForm?: (isFormDirty: boolean) => void;
}

export const MaritimeWarningsFormConnect: React.FC<
  MaritimeWarningsFormConnectProps
> = ({
  domain,
  onPreventCloseView,
  onModifyForm,
}: MaritimeWarningsFormConnectProps) => {
  const { t } = useWarningsTranslation();
  const { auth, isLoggedIn } = useAuthenticationContext();
  const confirmDialog = useConfirmationDialog();
  const [error, setError] = useState<
    { title: string; severity: AlertColor } | undefined
  >();

  const domainLabel = React.useMemo(
    () =>
      domain === 'coastalWaters'
        ? t('maritime-domain-coastal')
        : t('maritime-domain-northsea'),
    [domain, t],
  );

  const {
    data: maritimeConfig,
    isFetching: isFetchingConfig,
    error: configError,
    refetch: refetchConfig,
  } = useMaritimeWarningsConfig();
  const {
    data,
    isFetching: isFetchingWarnings,
    refetch: refetchWarnings,
    dataUpdatedAt,
  } = usePublicWarnings();
  const { mutateAsync: postWarnings, error: publishError } =
    usePostMaritimeWarnings();
  const { mutate: mutateEditor, error: editorError } =
    useMutateMaritimeWarningsEditor();

  const [hasFetched, setHasFetched] = useState(isFetchingWarnings);

  React.useEffect(() => {
    if (isFetchingWarnings) {
      setHasFetched(true);
    }
  }, [isFetchingWarnings]);

  const lastUpdateTime = dateUtils.dateToString(
    new Date(dataUpdatedAt),
    dateUtils.DATE_FORMAT_HOURS,
  );

  const areas = maritimeConfig?.districtList[domain];
  const shouldShowDistricts = domain === 'northSeaDistricts';
  const maritimeWarningsType = getType(domain);

  const [state, dispatch] = useMaritimeWarningsFormState();

  const warningsFromApi = React.useMemo(() => {
    return (
      data?.filter(
        (warning) =>
          warning.type === maritimeWarningsType &&
          warning.status === 'PUBLISHED',
      ) || []
    ).reverse() as MaritimeWarning[];
  }, [data, maritimeWarningsType]);

  const isNew = warningsFromApi.length === 0;
  const editor =
    warningsFromApi && warningsFromApi.length > 0
      ? warningsFromApi[0].editor
      : undefined;
  const previousEditor = usePrevious(editor);
  const isReadOnly = !isNew && editor !== auth?.username;
  const warningItemsFromApi = React.useMemo(
    () => mapMaritimeWarningsToItems(warningsFromApi || []),
    [warningsFromApi],
  );

  React.useEffect(() => {
    dispatch({
      type: 'initialize',
      warnings: warningItemsFromApi,
    });
  }, [dispatch, warningItemsFromApi]);

  React.useEffect(() => {
    onPreventCloseView?.(state.modified);
    if (onModifyForm) {
      onModifyForm(state.modified);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.modified]);
  usePreventBrowserClose(state.modified);

  const dispatchRedux = useDispatch();

  const showSnackbar = React.useCallback(
    (message: snackbarTypes.SnackbarMessage) => {
      dispatchRedux(snackbarActions.openSnackbar(message));
    },
    [dispatchRedux],
  );

  const onToggleSelectArea = React.useCallback(
    (id: string) => {
      React.startTransition(() => {
        dispatch({ type: 'toggleSelectArea', id });
      });
    },
    [dispatch],
  );

  const onSelectMultipleAreas = React.useCallback(
    (ids: string[]) => {
      React.startTransition(() => {
        dispatch({ type: 'selectMultipleAreas', ids });
      });
    },
    [dispatch],
  );

  const onAddSelectedAreas = React.useCallback(
    (id: string) => {
      React.startTransition(() => {
        dispatch({ type: 'addSelectedAreasToWarning', id });
      });
    },
    [dispatch],
  );

  const onRemoveWarning = React.useCallback(
    (id: string) => {
      React.startTransition(() => {
        dispatch({ type: 'removeWarning', id });
      });
    },
    [dispatch],
  );

  const onAddWarning = React.useCallback(() => {
    React.startTransition(() => {
      dispatch({ type: 'newWarning' });
    });
  }, [dispatch]);

  const onChangeWarning = React.useCallback(
    (id: string, newValues: MaritimeWarningItemAttributes) => {
      React.startTransition(() => {
        dispatch({ type: 'changeWarning', id, newValues });
      });
    },
    [dispatch],
  );

  const onRemoveArea = React.useCallback(
    (areaId: string) => {
      React.startTransition(() => {
        dispatch({ type: 'removeArea', areaId });
      });
    },
    [dispatch],
  );

  const onClear = React.useCallback(() => {
    React.startTransition(() => {
      dispatch({ type: 'clearWarnings' });
    });
  }, [dispatch]);

  const onMoveWarning = React.useCallback(
    (oldIndex: number, newIndex: number) => {
      dispatch({ type: 'moveWarning', oldIndex, newIndex });
    },
    [dispatch],
  );

  const onSubmit = React.useCallback(
    async (shouldExtend = false) => {
      dispatch({ type: 'mergeWarnings' });
      const nextState = reducer(state, { type: 'mergeWarnings' });
      const maritimeWarnings = mapItemsToMaritimeWarnings(
        nextState.warnings,
        domain,
        maritimeConfig!,
        auth?.username,
        shouldExtend,
      );

      await postWarnings(maritimeWarnings, {
        onSuccess: () => {
          dispatch({ type: 'submitWarnings' });
        },
      });
    },
    [dispatch, state, domain, maritimeConfig, auth?.username, postWarnings],
  );

  const onToggleReadOnly = React.useCallback(
    (isReadOnly: boolean) => {
      const message = isReadOnly
        ? t('maritime-editor-off', { domain: domainLabel })
        : t('maritime-editor-on', { domain: domainLabel });

      const updateEditor = (force: boolean): void =>
        mutateEditor(
          {
            domain: getType(domain),
            isEditor: !isReadOnly,
            force,
          },
          {
            onError: (e: Error) => {
              if (e.cause === 409) {
                void refetchWarnings();
                void confirmDialog({
                  title: t('warning-edit-mode-title'),
                  description: t('warning-edit-mode-description'),
                  confirmLabel: t('warning-edit-mode-confirm'),
                  cancelLabel: t('warning-dialog-cancel'),
                }).then(() => void updateEditor(true)); // force update editor
              } else {
                void refetchWarnings();
              }
            },
            onSuccess: async () => {
              void refetchWarnings();
              showSnackbar({
                type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
                message,
              });
            },
          },
        );

      void updateEditor(false);
    },
    [
      t,
      domainLabel,
      mutateEditor,
      domain,
      refetchWarnings,
      confirmDialog,
      showSnackbar,
    ],
  );

  useEffect(() => {
    // the BE_EDITOR_WARNING is handled in shouldGiveEditorWarning
    if (publishError && publishError.message !== BE_EDITOR_WARNING) {
      setError({
        title: publishError.message,
        severity: 'error',
      });
    } else if (editorError && editorError.cause !== 409) {
      setError({
        title: editorError.message,
        severity: 'error',
      });
    } else {
      setError(undefined);
    }
  }, [editorError, publishError]);

  const shouldGiveEditorWarning = React.useCallback(
    (newEditor?: string, oldEditor?: string): void => {
      const oldEditorIsYou = oldEditor && oldEditor === auth?.username;
      if (
        oldEditorIsYou &&
        (newEditor || publishError) &&
        newEditor !== oldEditor
      ) {
        setError({
          title: t('maritime-editor-off', { domain: domainLabel }),
          severity: 'warning',
        });
      }
    },
    [auth?.username, domainLabel, publishError, t],
  );

  useEffect(() => {
    // Only give editor warning when the change in editor is from a refetch
    if (hasFetched) {
      setHasFetched(false);
      shouldGiveEditorWarning(editor, previousEditor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!isLoggedIn) {
    return <AlertBanner title={t('error-auth-login')} />;
  }

  if (configError) {
    return (
      <AlertBanner
        actionButtonProps={{
          title: t('error-title-retry'),
          onClick: () => {
            void refetchConfig();
          },
        }}
        info={configError.message}
        title={t('maritime-config-error')}
      />
    );
  }

  return (
    <>
      {(!!isFetchingConfig || !!isFetchingWarnings || !maritimeConfig) && (
        <LinearProgress
          color="secondary"
          data-testid="loading-bar"
          sx={{ position: 'absolute', width: '100%', top: 0 }}
        />
      )}

      {!maritimeConfig ? (
        <div> {t('maritime-loading-config')}</div>
      ) : (
        <MaritimeWarningsForm
          areas={areas || []}
          editor={editor}
          error={error}
          isReadOnly={isReadOnly}
          lastUpdatedTime={lastUpdateTime}
          maritimeConfig={maritimeConfig}
          modified={state.modified}
          onAddSelectedAreas={onAddSelectedAreas}
          onAddWarning={onAddWarning}
          onChangeWarning={onChangeWarning}
          onClear={onClear}
          onMoveWarning={onMoveWarning}
          onRemoveArea={onRemoveArea}
          onRemoveWarning={onRemoveWarning}
          onSelectMultipleAreas={onSelectMultipleAreas}
          onSubmit={onSubmit}
          onToggleReadOnly={onToggleReadOnly}
          onToggleSelectArea={onToggleSelectArea}
          selectedAreaIds={state.selectedAreaIds}
          shouldHideEditorToggle={isNew}
          shouldShowDistricts={shouldShowDistricts}
          warnings={state.warnings}
        />
      )}
    </>
  );
};
